import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import type { Group, GroupMember } from '../../types/account';
import GroupMemberDialog from '../dialogs/GroupMemberDialog';

interface ListProps {
  groups: Group[];
  onChange: (next: Group[]) => void;
}

interface EditableListProps extends ListProps {
  minGroups?: number;
  submitAttempted?: boolean;
}

interface FullProps extends ListProps {
  showNotice?: boolean;
}

export const GroupsEditorToolbar: React.FC<ListProps> = ({ groups, onChange }) => {
  const translate = useTranslate();

  const addGroup = () => {
    onChange([...groups, { groupId: null, groupName: '', members: [] }]);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        gap: { xs: 1, sm: 0 },
      }}
    >
      <Button
        size="small"
        variant="contained"
        onClick={addGroup}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          width: { xs: '100%', sm: 'auto' },
        }}
      >
        + {translate('resources.accounts.groups.addGroup')}
      </Button>
    </Box>
  );
};

export const GroupsEditorList: React.FC<EditableListProps> = ({
  groups,
  onChange,
  minGroups = 0,
  submitAttempted = false,
}) => {
  const translate = useTranslate();
  const canRemove = groups.length > minGroups;

  const [touched, setTouched] = useState<Set<number>>(new Set());
  useEffect(() => {
    setTouched(new Set());
  }, [groups.length]);
  const markTouched = (index: number) =>
    setTouched((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });

  const [memberDialog, setMemberDialog] = useState<{
    open: boolean;
    groupIndex: number;
    memberIndex: number;
    initial?: GroupMember;
  }>({ open: false, groupIndex: -1, memberIndex: -1 });

  const removeGroup = (groupIndex: number) => {
    onChange(groups.filter((_, i) => i !== groupIndex));
  };

  const setGroupName = (groupIndex: number, name: string) => {
    onChange(
      groups.map((g, i) => (i === groupIndex ? { ...g, groupName: name } : g)),
    );
  };

  const submitMember = (member: GroupMember) => {
    const { groupIndex, memberIndex } = memberDialog;
    onChange(
      groups.map((g, i) => {
        if (i !== groupIndex) return g;
        const existing = g.members.findIndex((m) => m.userId === member.userId);
        let members;
        if (memberIndex >= 0) {
          members = g.members.map((m, j) => (j === memberIndex ? member : m));
        } else if (existing >= 0) {
          members = g.members.map((m, j) => (j === existing ? member : m));
        } else {
          members = [...g.members, member];
        }
        return { ...g, members };
      }),
    );
    setMemberDialog({ open: false, groupIndex: -1, memberIndex: -1 });
  };

  const removeMember = (groupIndex: number, memberIndex: number) => {
    onChange(
      groups.map((g, i) =>
        i === groupIndex
          ? { ...g, members: g.members.filter((_, j) => j !== memberIndex) }
          : g,
      ),
    );
  };

  return (
    <Box>
      {groups.map((g, gIndex) => {
        const nameMissing =
          (submitAttempted || touched.has(gIndex)) && !g.groupName?.trim();
        return (
        <Box
          key={gIndex}
          sx={{
            border: 1,
            borderColor: 'divider',
            p: { xs: 1.5, sm: 2 },
            mb: 2,
            borderRadius: 1,
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 0.5, sm: 1 },
              alignItems: 'flex-end',
            }}
          >
            <TextField
              label={translate('resources.accounts.groups.groupName')}
              fullWidth
              required
              size="small"
              value={g.groupName}
              onChange={(e) => setGroupName(gIndex, e.target.value)}
              onBlur={() => markTouched(gIndex)}
              InputLabelProps={{ shrink: true }}
              error={nameMissing}
              helperText={
                nameMissing
                  ? translate('resources.accounts.validation.required', {
                      field: translate('resources.accounts.groups.groupName'),
                    })
                  : undefined
              }
            />
            <IconButton
              onClick={() => removeGroup(gIndex)}
              aria-label="remove"
              disabled={!canRemove}
              color="error"
              sx={{ flexShrink: 0, mb: '5px' }}
            >
              <CloseIcon
                color={canRemove ? 'error' : 'disabled'}
                sx={{ fontSize: { xs: 18, sm: 22 } }}
              />
            </IconButton>
          </Box>
          <Button
            variant="contained"
            size="small"
            disabled={!g.groupName}
            onClick={() =>
              setMemberDialog({
                open: true,
                groupIndex: gIndex,
                memberIndex: -1,
                initial: undefined,
              })
            }
            sx={{ my: 1, textTransform: 'none', fontWeight: 500 }}
          >
            {translate('resources.accounts.groups.addMember')}
          </Button>
          {g.members.map((m, mIndex) => (
            <Box
              key={mIndex}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                p: 1,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                mb: 0.5,
              }}
            >
              <Typography
                sx={{
                  minWidth: 0,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                {m.userId}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                <IconButton
                  size="small"
                  onClick={() =>
                    setMemberDialog({
                      open: true,
                      groupIndex: gIndex,
                      memberIndex: mIndex,
                      initial: m,
                    })
                  }
                >
                  <EditIcon color="primary" fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => removeMember(gIndex, mIndex)}
                >
                  <CloseIcon color="error" fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
        );
      })}

      <GroupMemberDialog
        open={memberDialog.open}
        initial={memberDialog.initial}
        onCancel={() =>
          setMemberDialog({ open: false, groupIndex: -1, memberIndex: -1 })
        }
        onSubmit={submitMember}
      />
    </Box>
  );
};

export const GroupsEditor: React.FC<FullProps> = ({
  groups,
  onChange,
  showNotice = true,
}) => {
  const translate = useTranslate();
  return (
    <>
      {showNotice && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600 }}>
            {translate('resources.accounts.groups.noticeTitle')}
          </Typography>
          <Typography variant="body2">
            {translate('resources.accounts.groups.noticeBody')}
          </Typography>
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <GroupsEditorToolbar groups={groups} onChange={onChange} />
      </Box>

      <GroupsEditorList groups={groups} onChange={onChange} />
    </>
  );
};

export default GroupsEditor;
